import React, { memo, Suspense } from 'react';
import {
  Route, Routes, BrowserRouter as Router, Navigate,
} from 'react-router-dom';
import { lang } from 'common/lang';
import { linkPath } from 'common/constants';
import PersistLogin from 'features/PersistLogin/PersistLogin';
import { Loader, ScrollToTop } from 'common/designSystem';
import SessionHandler from 'features/sessionHandler';
import AppLifecycleHandler from 'features/PersistLogin/AppLifecycleHandler';
import StoriesBackdrop from 'features/Onboarding/stories/StoriesBackdrop';
import PartnerLink from 'features/Partner/PartnerLink';
// Dev-only preview; the lazy import sits behind a NODE_ENV check so CRA drops it from production.
const LifecyclePreview = process.env.NODE_ENV !== 'production'
  ? React.lazy(() => import('../DevPreview/LifecyclePreview'))
  : null;
const Welcome = React.lazy(() => import('../welcome'));
const Signup = React.lazy(() => import('../signup'));
const TermsPolicy = React.lazy(() => import('../TermsPolicy/TermsPolicy'));
const Verify = React.lazy(() => import('../verify'));
const Dashboard = React.lazy(() => import('../dashboard'));
const Pricing = React.lazy(() => import('../pricing'));
const Success = React.lazy(() => import('../success'));
const Login = React.lazy(() => import('../login'));
const ForgotPassword = React.lazy(() => import('../ForgotPassword'));
const ResetPassword = React.lazy(() => import('../ResetPassword'));
const Tutorial = React.lazy(() => import('../Tutorial'));
const UploadDocument = React.lazy(() => import('../UploadDocument'));
const RenewalDate = React.lazy(() => import('../UploadDocument/RenewalDate'));
const Documents = React.lazy(() => import('../Documents'));
const Reminders = React.lazy(() => import('../Reminders'));
const MenuBar = React.lazy(() => import('../MenuBar'));
const ManageCategories = React.lazy(() => import('../ManageCategories'));
const Upload = React.lazy(() => import('../UploadDocument/UploadFile'));
const UploadSuccess = React.lazy(() => import('../UploadDocument/UploadSuccess'));
const SearchDocument = React.lazy(() => import('../SearchDocument'));
const DocumentReview = React.lazy(() => import('../UploadDocument/DocumentReview'));
const UploadDVLA = React.lazy(() => import('../UploadDocument/UploadDVLA'));
const GiveItName = React.lazy(() => import('../UploadDocument/GiveItName'));
const FastScanLoader = React.lazy(() => import('../UploadDocument/FastScanLoader'));
const VehicleDetails = React.lazy(() => import('../UploadDocument/VehicleDetails'));
const ViewCategory = React.lazy(() => import('../Documents/ViewCategory'));
const BirthdaysScreen = React.lazy(() => import('../Birthdays/BirthdaysScreen'));
const BirthdayDetail = React.lazy(() => import('../Birthdays/BirthdayDetail'));
const ViewGroup = React.lazy(() => import('../Documents/ViewGroup'));
const GroupSelectionStep = React.lazy(() => import('../UploadDocument/GroupSelectionStep'));
const EditDocument = React.lazy(() => import('../Documents/EditDocument'));
const EditDocumentCategory = React.lazy(() => import('../Documents/EditCategory'));
const EditNotes = React.lazy(() => import('../Documents/EditNotes'));
// eslint-disable-next-line import/no-unresolved
const EditData = React.lazy(() => import('../Documents/EditData'));
// eslint-disable-next-line import/no-unresolved
const DataEntry = React.lazy(() => import('../UploadDocument/DataEntry'));
const UserProfile = React.lazy(() => import('../MenuBar/UserProfile'));
const PrivacyPolicy = React.lazy(() => import('../MenuBar/PrivacyPolicy'));
const AboutDoqit = React.lazy(() => import('../MenuBar/AboutDoqit'));
const FAQ = React.lazy(() => import('../MenuBar/FAQ'));
const VehicleList = React.lazy(() => import('../Vehicle'));
const Mfa = React.lazy(() => import('../mfa'));
// eslint-disable-next-line import/no-unresolved
const VehicleSelection = React.lazy(() => import('../UploadDocument/VehicleSelection'));
// eslint-disable-next-line import/no-unresolved
const ManualVehicleEntry = React.lazy(() => import('../UploadDocument/ManualVehicleEntry'));
// eslint-disable-next-line import/no-unresolved
const EditVehicle = React.lazy(() => import('../Vehicle/EditVehicle'));
// Property
const PropertyList = React.lazy(() => import('../Property'));
const PropertySelection = React.lazy(() => import('../UploadDocument/PropertySelection'));
const ManualPropertyEntry = React.lazy(() => import('../UploadDocument/ManualPropertyEntry'));
const PropertyDetails = React.lazy(() => import('../UploadDocument/PropertyDetails'));
// Sharing
const MySharing = React.lazy(() => import('../Sharing'));
const MySharedLinks = React.lazy(() => import('../Sharing/MySharedLinks'));
const AddSharingAccount = React.lazy(() => import('../Sharing/AddSharingAccount'));
// eslint-disable-next-line import/no-unresolved
const SharedWithMe = React.lazy(() => import('../Sharing/SharedWithMe'));
// eslint-disable-next-line import/no-unresolved
const SharedDocumentView = React.lazy(() => import('../Sharing/SharedDocumentView'));
const SharingTab = React.lazy(() => import('../SharingTab'));
// Onboarding
const OnboardingWhoFor = React.lazy(() => import('../Onboarding/screens/WhoForScreen'));
const OnboardingCareAbout = React.lazy(() => import('../Onboarding/screens/CareAboutScreen'));
const OnboardingStories = React.lazy(() => import('../Onboarding/screens/StoriesScreen'));
const OnboardingCost = React.lazy(() => import('../Onboarding/screens/CostProofScreen'));
const OnboardingHowWeHelp = React.lazy(() => import('../Onboarding/screens/HowWeHelpScreen'));
const OnboardingThreeSteps = React.lazy(() => import('../Onboarding/screens/ThreeStepsScreen'));
const OnboardingPrivacy = React.lazy(() => import('../Onboarding/screens/PrivacyTrustScreen'));
const OnboardingSocialProof = React.lazy(() => import('../Onboarding/screens/SocialProofScreen'));
const OnboardingAddItem = React.lazy(() => import('../Onboarding/screens/AddItemScreen'));
const OnboardingScanning = React.lazy(() => import('../Onboarding/screens/SmartScanLoaderScreen'));
const OnboardingSampleScan = React.lazy(() => import('../Onboarding/screens/SampleScanScreen'));
const OnboardingDoqReady = React.lazy(() => import('../Onboarding/screens/DoqReadyScreen'));
const OnboardingDocDetail = React.lazy(() => import('../Onboarding/screens/DocDetailScreen'));
const OnboardingNameItem = React.lazy(() => import('../Onboarding/screens/NameItemScreen'));
const OnboardingCategory = React.lazy(() => import('../Onboarding/screens/CategoryScreen'));
const OnboardingReminder = React.lazy(() => import('../Onboarding/screens/ReminderScreen'));
// Dev-only sample-scan review gallery — see the NODE_ENV guard below; never registered in a production build.
const OnboardingSampleGallery = React.lazy(() => import('../Onboarding/screens/SampleGalleryScreen'));

const LearnIndex = React.lazy(() => import('../Learn/LearnIndex'));
const LearnClipPage = React.lazy(() => import('../Learn/LearnClipPage'));
const OfferScreen = React.lazy(() => import('../Offer/OfferScreen'));
const FirstDocumentScreen = React.lazy(() => import('../FirstDocument/FirstDocumentScreen'));

const WebRoutes = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Router>
        <AppLifecycleHandler />
        <SessionHandler />
        <ScrollToTop />
        <Routes>
          {/* Public / guest routes — intentionally NOT wrapped in PersistLogin */}
          <Route path="/" element={<Welcome />} />
          <Route path={linkPath.signup} element={<Signup />} />
          <Route path={linkPath.terms} element={<TermsPolicy />} />
          <Route path={linkPath.login} element={<Login />} />
          <Route path={linkPath.pricing} element={<Pricing />} />
          <Route path={linkPath.privacyPolicy} element={<PrivacyPolicy />} />
          <Route path={linkPath.tutorial} element={<Tutorial />} />
          <Route path={linkPath.verify} element={<Verify />} />
          <Route path={linkPath.success} element={<Success />} />
          <Route path={linkPath.forgotPassword} element={<ForgotPassword />} />
          <Route path={linkPath.resetPasword} element={<ResetPassword />} />
          <Route path={linkPath.mfa} element={<Mfa />} />
          {LifecyclePreview && (
            <Route
              path="/dev/lifecycle"
              element={<Suspense fallback={null}><LifecyclePreview /></Suspense>}
            />
          )}
          {/* Partner links tag the person for the partner's paywall, then carry on. */}
          <Route path="/partner/:slug" element={<PartnerLink />} />
          {/* How-to videos: work signed in and out, so outside PersistLogin */}
          <Route path={linkPath.learn} element={<LearnIndex />} />
          <Route
            path={linkPath.learnClip}
            element={(
              <StoriesBackdrop>
                <Suspense fallback={null}>
                  <LearnClipPage />
                </Suspense>
              </StoriesBackdrop>
            )}
          />
          {/*
            Authed routes — ONE pathless PersistLogin layout route wraps them all.
            PersistLogin renders an <Outlet/>, so each child still matches its own
            exact path; the difference from the previous per-route wrappers is that
            navigating between these siblings no longer unmounts/remounts
            PersistLogin, so it no longer fires validate-token on every navigation.
          */}
          <Route element={<PersistLogin />}>
            <Route path={linkPath.offer} element={<OfferScreen />} />
            <Route path={linkPath.firstDocument} element={<FirstDocumentScreen />} />
            <Route path={linkPath.editCategory} element={<EditDocumentCategory />} />
            <Route path={linkPath.editNotes} element={<EditNotes />} />
            <Route path={linkPath.editData} element={<EditData />} />
            <Route path={linkPath.documentList} element={<Documents />} />
            <Route path={linkPath.reminder} element={<Reminders />} />
            <Route path={linkPath.menuBar} element={<MenuBar />} />
            <Route path={linkPath.manageCategories} element={<ManageCategories />} />
            <Route path={linkPath.dashboard} element={<Dashboard />} />
            <Route path={linkPath.documentTitle} element={<UploadDocument />} />
            <Route path={linkPath.documentName} element={<GiveItName />} />
            <Route path={linkPath.documentScanning} element={<FastScanLoader />} />
            <Route path={linkPath.documentUploadDvla} element={<UploadDVLA />} />
            <Route path={linkPath.documentVehicle} element={<VehicleDetails />} />
            <Route path={linkPath.documentRenewal} element={<RenewalDate />} />
            <Route path={linkPath.documentData} element={<DataEntry />} />
            <Route path={linkPath.searchDocumentPath} element={<SearchDocument />} />
            <Route path={linkPath.documentSuccess} element={<UploadSuccess />} />
            <Route path={linkPath.documentUpload} element={<Upload />} />
            <Route path={linkPath.documentReview} element={<DocumentReview />} />
            <Route path={linkPath.category} element={<ViewCategory />} />
            <Route path={linkPath.birthdays} element={<BirthdaysScreen />} />
            <Route path={linkPath.birthdayDetail} element={<BirthdayDetail />} />
            <Route path={linkPath.viewGroup} element={<ViewGroup />} />
            <Route path={linkPath.groupSelection} element={<GroupSelectionStep />} />
            <Route path={linkPath.editDocuments} element={<EditDocument />} />
            <Route path={linkPath.userProfile} element={<UserProfile />} />
            <Route path={linkPath.aboutDoqit} element={<AboutDoqit />} />
            <Route path={linkPath.faq} element={<FAQ />} />
            <Route path={linkPath.vehicleList} element={<VehicleList />} />
            <Route path={linkPath.vehicleSelection} element={<VehicleSelection />} />
            <Route path={linkPath.manualVehicleEntry} element={<ManualVehicleEntry />} />
            {/* Property Routes */}
            <Route path={linkPath.propertyList} element={<PropertyList />} />
            <Route path={linkPath.propertySelection} element={<PropertySelection />} />
            <Route path={linkPath.manualPropertyEntry} element={<ManualPropertyEntry />} />
            <Route path={linkPath.propertyDetails} element={<PropertyDetails />} />
            <Route path={linkPath.addProperty} element={<ManualPropertyEntry />} />
            <Route path={linkPath.editProperty} element={<ManualPropertyEntry />} />
            <Route path={linkPath.addVehicle} element={<ManualVehicleEntry />} />
            <Route path={linkPath.editVehicle} element={<EditVehicle />} />
            {/* Sharing Routes */}
            <Route path={linkPath.mySharing} element={<MySharing />} />
            <Route path={linkPath.mySharedLinks} element={<MySharedLinks />} />
            <Route path={linkPath.addSharingAccount} element={<AddSharingAccount />} />
            <Route path={linkPath.sharedWithMe} element={<SharedWithMe />} />
            <Route path={linkPath.sharedDocument} element={<SharedDocumentView />} />
            <Route path={linkPath.sharedDocumentByDoc} element={<SharedDocumentView />} />
            {/* Sharing Tab */}
            <Route path={linkPath.sharingTab} element={<SharingTab />} />
          </Route>
          {/* Onboarding Routes - Public (guest session, no auth required) */}
          {/* Hidden from flow 2026-07-04 (product call) — re-entry point when profiles return */}
          <Route path={linkPath.onboardingWhoFor} element={<OnboardingWhoFor />} />
          <Route path={linkPath.onboardingCareAbout} element={<OnboardingCareAbout />} />
          <Route
            path={linkPath.onboardingStories}
            element={(
              // Non-lazy dark backdrop + its own Suspense, so the light app
              // loader never flashes before the dark stories screen.
              <StoriesBackdrop>
                <Suspense fallback={null}>
                  <OnboardingStories />
                </Suspense>
              </StoriesBackdrop>
            )}
          />
          <Route path={linkPath.onboardingCost} element={<OnboardingCost />} />
          <Route path={linkPath.onboardingHowWeHelp} element={<OnboardingHowWeHelp />} />
          <Route path={linkPath.onboardingThreeSteps} element={<OnboardingThreeSteps />} />
          <Route path={linkPath.onboardingPrivacy} element={<OnboardingPrivacy />} />
          <Route path={linkPath.onboardingSocialProof} element={<OnboardingSocialProof />} />
          <Route path={linkPath.onboardingAddItem} element={<OnboardingAddItem />} />
          <Route path={linkPath.onboardingScanning} element={<OnboardingScanning />} />
          <Route path={linkPath.onboardingSampleScan} element={<OnboardingSampleScan />} />
          <Route path={linkPath.onboardingDoqReady} element={<OnboardingDoqReady />} />
          <Route path={linkPath.onboardingDocDetail} element={<OnboardingDocDetail />} />
          <Route path={linkPath.onboardingNameItem} element={<OnboardingNameItem />} />
          <Route path={linkPath.onboardingCategory} element={<OnboardingCategory />} />
          <Route path={linkPath.onboardingReminder} element={<OnboardingReminder />} />
          {/* DEV-ONLY sample-scan review gallery for judging all 4 personas' letters + animation
              side by side. CRA sets NODE_ENV to 'production' in real builds, so this route is
              tree-shaken out of anything shipped — it can never appear to a real user (same
              pattern as pricing/index.tsx's dev-only paywall skip). */}
          {process.env.NODE_ENV === 'development' && (
            <Route path={linkPath.onboardingSampleGallery} element={<OnboardingSampleGallery />} />
          )}
          {/* Catch-all. Without this, any URL that matches no route above
              renders literally nothing - a blank white screen with no error,
              which is indistinguishable from a crash to whoever reports it.
              That is how the shared-document link bug (869egr36n) presented,
              and it cost a lot of time to tell apart from the genuine
              rendering white screen. Send them to "/" rather than straight to
              the dashboard: Welcome already forwards a signed-in user on and
              leaves a signed-out one where they belong, so this works for
              both without duplicating that decision here. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </Suspense>
  );
};

export default memo(WebRoutes);
